"use no memo";

import { type MouseEvent, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";

import { t } from "i18next";

import { Menu as MenuIcon, Notifications } from "@mui/icons-material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ContactPageIcon from "@mui/icons-material/ContactPage";
import InfoIcon from "@mui/icons-material/Info";
import LogoutIcon from "@mui/icons-material/Logout";
import SettingsIcon from "@mui/icons-material/Settings";
import {
  AppBar,
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  MenuList,
  Toolbar,
  useMediaQuery
} from "@mui/material";
import { type Theme } from "@mui/material/styles";

import { theme } from "@/src/theme/theme";

import { badgesAtom } from "@/src/state/badgesAtom";

import { LoginSource } from "@/src/auth/auth";
import { useAuth } from "@/src/auth/hooks/useAuth";
import { useGetAuctionsSummary } from "@/src/queries/dealer/getAuctionsSummary";
import { useGetNotifications } from "@/src/queries/notifications/notificationQueries";
import { IoTEventType } from "@/src/types/types";
import { checkForNewPatches } from "@/src/utils/patchUtils";
import { useResetSearchTerms } from "@/src/utils/searchTermUtils";

import LanguageSwitcher from "../../../localizations/languageSwitcher";
import { notificationAtom } from "../../../state/notificationAtom";
import { BaanaLogo } from "../../atoms";
import NotificationMenu from "../NotificationCenter/NotificationMenu";
import HamburgerMenuItem from "./HamburgerMenuItem";
import HeaderMenuItem from "./HeaderMenuItem";
import UserInfo from "./UserInfo";

import { useGTMDispatch } from "@elgorditosalsero/react-gtm-hook";
import { useAtom, useSetAtom } from "jotai";

const dealerHeaderButtonHoverShadowSx = {
  boxShadow:
    "0px 2px 4px -1px rgb(0 0 0 / 20%), 0px 4px 5px 0px rgb(0 0 0 / 14%), 0px 1px 10px 0px rgb(0 0 0 / 12%)"
} as const;

const dealerHeaderLoginButtonSx = (theme: Theme) => ({
  fontSize: "0.875rem",
  fontWeight: theme.typography.fontWeightBold,
  backgroundColor: theme.palette.common.white,
  color: theme.palette.primary.main,
  height: "36px",
  "&:hover": {
    backgroundColor: theme.palette.grey[200],
    ...dealerHeaderButtonHoverShadowSx
  }
});

const DealerHeader = () => {
  const navigate = useNavigate();
  const resetSearchTerms = useResetSearchTerms();
  const setNotification = useSetAtom(notificationAtom);
  const { isAuthenticated, isAuthenticating, user, login, logout, signup } =
    useAuth();
  const sendDataToGTM = useGTMDispatch();
  const isLargeScreen = useMediaQuery("(min-width:935px)");

  const [dealerMenuToggleButtonElement, setDealerMenuToggleButton] =
    useState<null | HTMLElement>(null);
  const menuOpen = Boolean(dealerMenuToggleButtonElement);

  // User menu icon
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuOpen = (event: MouseEvent<HTMLButtonElement>) => {
    setDealerMenuToggleButton(event.currentTarget);
  };

  const handleMenuClose = () => {
    setDealerMenuToggleButton(null);
  };

  const [badges, setBadges] = useAtom(badgesAtom);

  useEffect(() => {
    if (isAuthenticated) {
      const checkPatches = async () => {
        const hasNewPatches = await checkForNewPatches();
        if (hasNewPatches) {
          setBadges((currentBadges) => {
            return [...currentBadges, { type: IoTEventType.NEW_PATCH }];
          });
        }
      };
      checkPatches();
    }
  }, [isAuthenticated]);

  const hasNewMessageBadge =
    badges.filter((badgeItem) => badgeItem.type === IoTEventType.NEW_PATCH)
      .length > 0;

  const { data: summary } = useGetAuctionsSummary(isAuthenticated);
  const { data: notifications } = useGetNotifications(isAuthenticated);

  const unreadNotifications = notifications?.filter(
    ({ isRead }) => !isRead
  ).length;

  const numberOfMyAuctionsRequiringAttention = summary?.started ?? 0;
  const numberOfEndedAuctionsRequiringAttention = summary
    ? summary.waitingForSeller + summary.inNegotiation + summary.accepted
    : 0;

  const location = useLocation();

  const handleNavigation = (url: string) => {
    navigate(url);
    handleMenuClose();
  };

  const handleMobileAuctionToggle = () => {
    const currentPath = location.pathname;
    if (currentPath === "/dealer/all-active") {
      handleNavigation("/dealer/my");
    } else {
      handleNavigation("/dealer/all-active");
    }
  };

  useEffect(() => {
    if (
      location.pathname === "/dealer/all-active" ||
      location.pathname.startsWith("/dealer/auction/")
    ) {
      return;
    } else {
      localStorage.removeItem("selectedSearchFilter");
    }
  }, [location]);

  // Resolve dealer and dealer group names
  let companyName: string | undefined = undefined;
  if (user?.business?.marketingName) {
    companyName = user.business.marketingName;
    if (user?.business?.name) {
      companyName = `${companyName} (${user.business.name})`;
    }
  } else if (user?.business?.name) {
    companyName = user.business.name;
  } else {
    // Do nothing
  }

  const currentUrl = location.pathname;

  // Unauthenticated header items
  const headerItemContactInfoUnAuthorized = (
    <HeaderMenuItem
      id="dealerMenuContactInfoMenuItem"
      onClick={() => {
        handleMenuClose();
        handleNavigation("/dealer/contact-info");
      }}
      isActive={currentUrl === "/dealer/contact-info"}
      label={t("general.contactInfo")}
    />
  );

  // Authenticated/UnAuthenticated Header items on all page
  const headerLogoButton = (
    <Box
      id="dealerLogoButton"
      sx={{
        display: "flex",
        alignItems: "center",
        cursor: "pointer",
        pt: 1
      }}
      onClick={() => {
        handleNavigation("/dealer");
        resetSearchTerms();
      }}
    >
      <Box sx={{ width: "6rem" }}>
        <BaanaLogo color={theme.palette.semantic.primary.contrastText} />
      </Box>
    </Box>
  );

  const mobileAuctionToggleButton = (
    <Button
      id="mobileAuctionToggleButton"
      onClick={handleMobileAuctionToggle}
      variant="outlined"
      color="primary"
      sx={{
        fontSize: "0.75rem",
        fontWeight: theme.typography.fontWeightBold,
        backgroundColor: theme.palette.common.white,
        color: theme.palette.semantic.primary.main,
        borderColor: theme.palette.semantic.primary.main,
        borderWidth: 1,
        "&:hover": {
          backgroundColor: theme.palette.common.white,
          borderColor: theme.palette.semantic.primary.dark,
          borderWidth: 1,
          ...dealerHeaderButtonHoverShadowSx
        },
        p: 1,
        padding: "0px 8px 0px 8px",
        height: "32px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center"
      }}
    >
      {location.pathname === "/dealer/all-active"
        ? t("dealer.menu.myAuctions")
        : t("dealer.menu.allActiveAuctions")}
    </Button>
  );

  // Authenticated header items
  const headerItemNavToConsumerSite = (
    <HeaderMenuItem
      id="dealerMenuNavToConsumerSiteItem"
      onClick={() => {
        setNotification(null);
        // Hard page reload needed to refresh the Alma Tunnus component
        localStorage.setItem("USAGE_MODE", "consumer");
        window.location.href = "/";
      }}
      label={t("menus.toConsumerView")}
    />
  );

  const headerItemLogin = (
    <Button
      id={
        isLargeScreen ? "dealerHeaderButtonLogin" : "dealerHeaderMenuItemLogin"
      }
      onClick={() => {
        sendDataToGTM({
          event: "login_header_clicked",
          user_type: "dealer"
        });
        if (isLargeScreen) {
          handleMenuClose();
        }
        setNotification(null);
        login(
          "dealer",
          isLargeScreen
            ? LoginSource.DEALER_HEADER_LOGIN
            : LoginSource.DEALER_HEADER_LOGIN
        );
      }}
      variant="contained"
      sx={dealerHeaderLoginButtonSx}
    >
      {t("general.login")}
    </Button>
  );

  const headerItemRegister = (
    <HeaderMenuItem
      id="dealerMenuRegisterMenuItem"
      onClick={() => {
        handleMenuClose();
        setNotification(null);
        signup("dealer", LoginSource.DEALER_HEADER_SIGNUP);
      }}
      label={t("general.registerDealer")}
    />
  );

  // Authenticated header items for both mobile and dekstop
  const headerItemUserInfo = (
    <UserInfo
      firstName={user?.firstName}
      lastName={user?.lastName}
      companyName={companyName}
      sx={{ textTransform: "none" }}
    />
  );

  const headerItemUserSettings = (
    <MenuItem
      id="dealerUserSettingsMenuItem"
      onClick={() => {
        handleMenuClose();
        handleClose();
        handleNavigation("/dealer/user-settings");
        resetSearchTerms();
      }}
    >
      <ListItemIcon>
        <SettingsIcon fontSize="small" />
      </ListItemIcon>
      <ListItemText sx={{ whiteSpace: "normal" }}>
        {t("general.userSettings")}
      </ListItemText>
    </MenuItem>
  );

  const headerItemInstructions = (
    <MenuItem
      id="dealerInstructionsMenuItem"
      onClick={() => {
        handleMenuClose();
        handleClose();
        handleNavigation("/dealer/instructions");
        resetSearchTerms();
      }}
    >
      <ListItemIcon>
        <InfoIcon fontSize="small" />
      </ListItemIcon>
      <ListItemText>
        <Box
          sx={{ display: "flex", alignItems: "center", position: "relative" }}
        >
          {t("dealer.menu.instructionsAndUpdates")}
          {hasNewMessageBadge && (
            <Badge
              variant="standard"
              color="info"
              badgeContent="1"
              sx={{
                width: 10,
                height: 10,
                top: 3,
                right: -3,
                "& .MuiBadge-badge": {
                  borderRadius: "60%"
                }
              }}
            />
          )}
        </Box>
      </ListItemText>
    </MenuItem>
  );
  const headerItemContactInfo = (
    <MenuItem
      id="dealerMenuContactInfoMenuItem"
      onClick={() => {
        handleMenuClose();
        handleClose();
        handleNavigation("/dealer/contact-info");
        resetSearchTerms();
      }}
    >
      {isAuthenticated && (
        <ListItemIcon>
          <ContactPageIcon fontSize="small" />
        </ListItemIcon>
      )}

      <ListItemText>{t("general.contactInfo")}</ListItemText>
    </MenuItem>
  );

  const headerItemLogOut = (
    <MenuItem
      id="dealerMenuLogoutMenuItem"
      onClick={() => {
        handleMenuClose();
        handleClose();
        setNotification(null);
        logout();
      }}
    >
      <ListItemIcon>
        <LogoutIcon fontSize="small" sx={{ ml: 0.25 }} />
      </ListItemIcon>
      <ListItemText>{t("general.logout")}</ListItemText>
    </MenuItem>
  );

  // Header items for desktop only

  const headerItemMyAuctions = (
    <HeaderMenuItem
      id="dealerMenuMyAuctionsMenuItem"
      onClick={() => {
        handleMenuClose();
        handleNavigation("/dealer/my");
        resetSearchTerms();
      }}
      isActive={currentUrl === "/dealer/my"}
      label={t("dealer.menu.myAuctions")}
      attentionCount={
        numberOfMyAuctionsRequiringAttention
          ? numberOfMyAuctionsRequiringAttention
          : undefined
      }
    />
  );

  const headerItemAllActiveAuctions = (
    <HeaderMenuItem
      id="dealerMenuAllActiveAuctionsMenuItem"
      onClick={() => {
        handleMenuClose();
        handleNavigation("/dealer/all-active");
        resetSearchTerms();
      }}
      isActive={currentUrl === "/dealer/all-active"}
      label={t("dealer.menu.allActiveAuctions")}
    />
  );

  const isEndedAuctionsActive = [
    "/dealer/pending",
    "/dealer/negotiations",
    "/dealer/accepted",
    "/dealer/declined",
    "/dealer/completed",
    "/dealer/closed",
    "/dealer/all-ended"
  ].includes(currentUrl);

  const headerItemEndedAuctions = (
    <HeaderMenuItem
      id="dealerMenuEndedAuctionsMenuItem"
      onClick={() => {
        handleMenuClose();
        handleNavigation("/dealer/all-ended");
        resetSearchTerms();
      }}
      isActive={isEndedAuctionsActive}
      label={t("dealer.menu.endedAuctions")}
      attentionCount={
        numberOfEndedAuctionsRequiringAttention
          ? numberOfEndedAuctionsRequiringAttention
          : undefined
      }
    />
  );

  const headerItemUserMenu = (
    <>
      <IconButton
        sx={{
          mr: 2,
          backgroundColor: open ? theme.palette.misc.outlineBorder : undefined
        }}
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        <Badge
          variant="standard"
          color="info"
          badgeContent={hasNewMessageBadge ? "1" : undefined}
          sx={{
            "& .MuiBadge-badge": {
              borderRadius: "60%",
              right: 4,
              top: 3,
              minWidth: "18px",
              height: "18px",
              fontSize: (theme) => theme.typography.caption.fontSize,
              px: 0.5,
              py: 0
            }
          }}
        >
          <AccountCircleIcon
            sx={{ color: theme.palette.semantic.primary.contrastText }}
          />
        </Badge>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right"
        }}
        onClose={handleClose}
      >
        <MenuList
          sx={{
            ".MuiListItemText-primary": {
              cursor: "pointer"
            }
          }}
        >
          <MenuItem
            sx={{
              "&:hover": {
                backgroundColor: "transparent",
                cursor: "default"
              }
            }}
          >
            {isAuthenticated && !isAuthenticating && headerItemUserInfo}
          </MenuItem>
          <Divider />
          {headerItemUserSettings}
          {headerItemInstructions}
          {headerItemContactInfo}
          {headerItemLogOut}
        </MenuList>
      </Menu>
    </>
  );

  // Header items for mobile only
  const headerMobileItemMyAuctions = (
    <HamburgerMenuItem
      id="dealerMenuMyAuctionsMenuItem"
      onClick={() => {
        handleMenuClose();
        handleNavigation("/dealer/my");
      }}
      label={t("dealer.menu.myAuctions")}
      attentionCount={
        numberOfMyAuctionsRequiringAttention
          ? numberOfMyAuctionsRequiringAttention
          : undefined
      }
    />
  );

  const headerMobileItemAllActiveAuctions = (
    <HamburgerMenuItem
      id="dealerMenuAllActiveAuctionsMenuItem"
      onClick={() => {
        handleMenuClose();
        handleNavigation("/dealer/all-active");
      }}
      label={t("dealer.menu.allActiveAuctions")}
    />
  );

  const headerMobileItemEndedAuctions = (
    <HamburgerMenuItem
      id="dealerMenuEndedAuctionsMenuItem"
      onClick={() => {
        handleMenuClose();
        handleNavigation("/dealer/all-ended");
      }}
      label={t("dealer.menu.endedAuctions")}
      attentionCount={
        numberOfEndedAuctionsRequiringAttention
          ? numberOfEndedAuctionsRequiringAttention
          : undefined
      }
    />
  );

  const headerMobileItemNotification = (
    <MenuItem
      id="dealerNotificationsMenuItem"
      onClick={() => {
        handleMenuClose();
        handleNavigation("/dealer/notifications");
        resetSearchTerms();
      }}
    >
      <Notifications
        sx={{ mr: 1, color: theme.palette.text.secondary }}
        fontSize="small"
      />
      <Badge
        variant="standard"
        color="secondary"
        badgeContent={unreadNotifications}
        sx={{
          "& .MuiBadge-badge": {
            top: 10,
            right: -19
          }
        }}
      >
        {t("components.notifications.notifications")}
      </Badge>
    </MenuItem>
  );

  return (
    <Box>
      <AppBar position="fixed" id="dealerHeader">
        <Toolbar
          sx={{
            justifyContent: "space-between"
          }}
        >
          {headerLogoButton}
          {/* Dealer desktop header view*/}
          {isLargeScreen ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center"
              }}
            >
              {!isAuthenticating && (
                <Box
                  id="dealerMenu"
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    "& .MuiMenuItem-root, & .MuiListItemButton-root": {
                      textTransform: "uppercase",
                      fontWeight: theme.typography.fontWeightBold
                    }
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: isLargeScreen && "row"
                    }}
                  >
                    {/* UnAuthenticated dealer header */}
                    {!isAuthenticated && (
                      <>
                        {headerItemNavToConsumerSite}
                        {headerItemContactInfoUnAuthorized}
                        {headerItemRegister}
                        {headerItemLogin}
                        <LanguageSwitcher />
                      </>
                    )}
                    {/* Authenticated dealer header */}
                    {isAuthenticated && (
                      <>
                        {headerItemAllActiveAuctions}
                        {headerItemMyAuctions}
                        {headerItemEndedAuctions}
                        <Box sx={{ mr: 1 }}>
                          <NotificationMenu />
                        </Box>
                        <LanguageSwitcher />
                        {headerItemUserMenu}
                      </>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {!isAuthenticated && headerItemLogin}
              {isAuthenticated && mobileAuctionToggleButton}

              <IconButton
                color="inherit"
                aria-label="menu"
                onClick={handleMenuOpen}
              >
                <Badge
                  badgeContent={!menuOpen ? unreadNotifications : undefined}
                  variant="standard"
                  color="secondary"
                >
                  <MenuIcon />
                </Badge>
              </IconButton>
            </Box>
          )}
        </Toolbar>
      </AppBar>
      <Toolbar />
      {/* Dealer Mobile header header */}
      {!isLargeScreen && (
        <Menu
          id="dealerMenu"
          anchorEl={dealerMenuToggleButtonElement}
          open={menuOpen}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right"
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right"
          }}
          sx={{
            ".MuiPopover-paper": { borderRadius: "6px" },
            width: "300px"
          }}
          slotProps={{
            list: {
              "aria-labelledby": "dealerMenuToggleButton",
              sx: {
                display: "flex",
                flexDirection: "column"
              }
            }
          }}
        >
          <MenuList sx={{ p: 0 }}>
            {/* UnAuthenticated dealer header */}
            {!isAuthenticated && (
              <Box>
                {headerItemNavToConsumerSite}
                <Divider />
                {headerItemContactInfo}
                {headerItemRegister}
                <LanguageSwitcher />
              </Box>
            )}
            {/*Authenticated dealer header */}
            {isAuthenticated && (
              <Box>
                {isAuthenticated && !isAuthenticating && (
                  <Box sx={{ px: 2, py: 1 }}> {headerItemUserInfo}</Box>
                )}
                <Divider />
                {headerMobileItemNotification}
                <Divider />
                {headerMobileItemAllActiveAuctions}
                {headerMobileItemMyAuctions}
                {headerMobileItemEndedAuctions}
                <Divider />
                {headerItemUserSettings}
                {headerItemInstructions}
                {headerItemContactInfo}
                {headerItemLogOut}
                <LanguageSwitcher />
              </Box>
            )}
          </MenuList>
        </Menu>
      )}
    </Box>
  );
};

export default DealerHeader;
